import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PERM, hasGlobalPermission } from "@/lib/permissions";
import { AlertTriangle } from "lucide-react";

/**
 * Warns System Administrators when the Keycloak realm is not configured the
 * way the user/password flow needs (see backend/keycloak_realm_setup.py).
 * Renders nothing for anyone else, when Keycloak admin access is not
 * configured, or when the check cannot run — a warning that cries wolf on a
 * transient error would be worse than none. "Unknown" checks do not count.
 */
export function KeycloakRealmBanner() {
  const { user } = useAuth();
  const allowed = hasGlobalPermission(user, PERM.SYSTEM_KEYCLOAK_MANAGE);
  const [failing, setFailing] = useState([]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    client
      .get("/admin/keycloak-settings/realm-config")
      .then(({ data }) => {
        if (!cancelled) setFailing(data.checks.filter((c) => c.ok === false));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  if (!allowed || failing.length === 0) return null;

  return (
    <div
      data-testid="keycloak-realm-banner"
      className="flex items-center justify-center gap-2 border-b border-amber-500/40 bg-amber-500/10 px-4 py-1.5 text-sm"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
      <span>
        The Keycloak realm needs configuration ({failing.length} {failing.length === 1 ? "check" : "checks"} failing).
      </span>
      <Link to="/settings" className="font-medium underline underline-offset-2">
        Review in Settings
      </Link>
    </div>
  );
}
